import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, LogOut, RefreshCw, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";

import { API } from "../utils/api";
const TOKEN_KEY = "us_admin_token";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [leads, setLeads] = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(false);

  const fetchLeads = useCallback(
    async (activeToken) => {
      setLeadsLoading(true);
      try {
        const { data } = await axios.get(`${API}/leads`, {
          headers: { Authorization: `Bearer ${activeToken}` },
        });
        setLeads(data);
        try {
          const { data: users } = await axios.get(`${API}/admin/tryon/users`, {
            headers: { Authorization: `Bearer ${activeToken}` },
          });
          setUsageUsers(users);
        } catch {}
        try {
          const { data: plansData } = await axios.get(`${API}/admin/plans`, {
            headers: { Authorization: `Bearer ${activeToken}` },
          });
          setPlans(plansData);
        } catch {}
      } catch (err) {
        if (err.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setLeads(null);
        }
      } finally {
        setLeadsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      setAuthError(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Неверный пароль"
      );
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
  };

  const [granting, setGranting] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [masterToggling, setMasterToggling] = useState(null);
  const [usageUsers, setUsageUsers] = useState(null);
  const [grantingKey, setGrantingKey] = useState(null);
  const [keyAmounts, setKeyAmounts] = useState({});
  const [plans, setPlans] = useState(null);
  const [planSaving, setPlanSaving] = useState(null);
  const [accQuery, setAccQuery] = useState("");
  const [accResults, setAccResults] = useState(null);
  const [accSearching, setAccSearching] = useState(false);
  const [accPlanChoice, setAccPlanChoice] = useState({});
  const [accGranting, setAccGranting] = useState(null);

  const searchAccounts = async () => {
    if (accSearching || accQuery.trim().length < 2) return;
    setAccSearching(true);
    try {
      const { data } = await axios.get(`${API}/admin/accounts`, {
        params: { q: accQuery.trim() },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccResults(data);
    } catch {
      toast.error("Не удалось найти пользователей");
    } finally {
      setAccSearching(false);
    }
  };

  const grantPlan = async (acc, i) => {
    const planId = accPlanChoice[acc.id] || (plans && plans[0]?.id);
    if (!planId) return;
    setAccGranting(i);
    try {
      const { data } = await axios.post(
        `${API}/admin/accounts/grant`,
        { account_id: acc.id, plan_id: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Тариф выдан: ${data.account.plan} · ${data.account.plan_period}`);
      setAccResults((prev) => prev.map((a, idx) => (idx === i ? data.account : a)));
    } catch (err) {
      toast.error(
        typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Не удалось выдать тариф"
      );
    } finally {
      setAccGranting(null);
    }
  };

  const accLabel = (a) => {
    const parts = [];
    if (a.name) parts.push(a.name);
    if (a.username) parts.push(`@${a.username}`);
    else if (a.telegram_id) parts.push(`tg ${a.telegram_id}`);
    if (a.email) parts.push(a.email);
    if (a.phone) parts.push(a.phone);
    return parts.join(" · ") || a.id;
  };

  const updatePlanField = (i, field, value) =>
    setPlans((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));

  const savePlan = async (p, i) => {
    setPlanSaving(i);
    try {
      await axios.put(
        `${API}/admin/plans/${p.id}`,
        {
          title: String(p.title),
          period: String(p.period),
          months: Number(p.months),
          generations: Number(p.generations),
          price_rub: Number(p.price_rub),
          is_pro: !!p.is_pro,
          active: !!p.active,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Тариф сохранён");
    } catch {
      toast.error("Не удалось сохранить тариф");
    } finally {
      setPlanSaving(null);
    }
  };

  const usageLabel = (u) => {
    if (u.label) return u.phone ? `${u.label} · ${u.phone}` : u.label;
    const src = u.key.startsWith("tg_") ? "Telegram" : u.key.startsWith("ph_") ? "сайт" : "устройство";
    if (u.phone) return `${u.phone} · ${src}`;
    if (u.key.startsWith("tg_")) return `Telegram · id ${u.key.slice(3)}`;
    if (u.key.startsWith("ph_")) return u.key.slice(3);
    return `Устройство · ${u.key.slice(4, 12)}…`;
  };

  const grantByKey = async (u, idx) => {
    const amount = Math.max(1, Math.min(parseInt(keyAmounts[u.key] || "5", 10) || 5, 100));
    setGrantingKey(idx);
    try {
      await axios.post(
        `${API}/admin/tryon/grant`,
        { key: u.key, amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Добавлено примерок: ${amount}`);
      fetchLeads(token);
    } catch {
      toast.error("Не удалось добавить примерки");
    } finally {
      setGrantingKey(null);
    }
  };

  const toggleMaster = async (lead, idx) => {
    setMasterToggling(idx);
    try {
      await axios.post(
        `${API}/admin/master`,
        { phone: lead.phone, master: !lead.is_master },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(lead.is_master ? "Платный доступ убран" : "Платный доступ выдан");
      fetchLeads(token);
    } catch {
      toast.error("Не удалось изменить доступ мастера");
    } finally {
      setMasterToggling(null);
    }
  };

  const grantTryon = async (lead, idx) => {
    const amount = Math.max(1, Math.min(parseInt(amounts[lead.id] || "5", 10) || 5, 100));
    setGranting(idx);
    try {
      await axios.post(
        `${API}/admin/tryon/grant`,
        { phone: lead.phone, amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Добавлено примерок: ${amount}`);
      fetchLeads(token);
    } catch {
      toast.error("Не удалось добавить примерки");
    } finally {
      setGranting(null);
    }
  };

  if (!token) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-6"
        data-testid="admin-login-screen"
      >
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm border border-white/10 bg-[#0A0A0A]/60 p-10 backdrop-blur-2xl"
        >
          <span className="mb-8 flex h-12 w-12 items-center justify-center rounded-full border border-[#C2A878]/40">
            <Lock size={18} strokeWidth={1.5} className="text-[#C2A878]" />
          </span>
          <h1 className="font-cormorant text-3xl font-light text-[#F4F4F0]">
            Доступ для владельца
          </h1>
          <p className="mt-3 text-sm text-[#A3A3A3]">Введите пароль, чтобы увидеть заявки.</p>

          <form onSubmit={handleLogin} className="mt-10" data-testid="admin-login-form">
            <label
              htmlFor="admin-password"
              className="mb-3 block font-jetbrains text-xs uppercase tracking-[0.25em] text-[#A3A3A3]"
            >
              Пароль
            </label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full border-b-2 border-white/10 bg-transparent py-3 text-lg font-light text-[#F4F4F0] focus:border-[#C2A878] focus:outline-none transition-colors duration-500"
              data-testid="admin-password-input"
            />
            {authError && (
              <p className="mt-3 text-sm text-red-400/80" data-testid="admin-login-error">
                {authError}
              </p>
            )}
            <button
              type="submit"
              disabled={authLoading || !password}
              className="mt-8 flex w-full items-center justify-center gap-3 border border-[#C2A878] py-3.5 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A] disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="admin-login-submit"
            >
              {authLoading ? <Loader2 size={14} className="animate-spin" /> : "Войти"}
            </button>
          </form>

          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 font-jetbrains text-xs uppercase tracking-[0.2em] text-[#525252] transition-colors duration-500 hover:text-[#C2A878]"
            data-testid="admin-back-link"
          >
            <ArrowLeft size={12} strokeWidth={1.5} />
            На сайт
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-6 py-16 md:px-12 lg:px-24" data-testid="admin-dashboard">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-white/10 pb-8">
          <div>
            <p className="mb-3 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
              ( Админ )
            </p>
            <h1 className="font-cormorant text-4xl font-light text-[#F4F4F0] md:text-5xl">
              Заявки
              {leads && (
                <span className="ml-4 font-jetbrains text-sm tracking-[0.2em] text-[#525252]" data-testid="admin-leads-count">
                  {leads.length}
                </span>
              )}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => fetchLeads(token)}
              className="flex items-center gap-2 border border-white/15 px-5 py-2.5 font-jetbrains text-xs uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-500 hover:border-[#C2A878] hover:text-[#C2A878]"
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={13} strokeWidth={1.5} className={leadsLoading ? "animate-spin" : ""} />
              Обновить
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 border border-white/15 px-5 py-2.5 font-jetbrains text-xs uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-500 hover:border-red-400/60 hover:text-red-400"
              data-testid="admin-logout-button"
            >
              <LogOut size={13} strokeWidth={1.5} />
              Выйти
            </button>
          </div>
        </div>

        <div className="mt-10">
          {leads === null ? (
            <div className="flex items-center gap-3 py-20 text-[#525252]" data-testid="admin-loading">
              <Loader2 size={16} className="animate-spin" />
              <span className="font-jetbrains text-xs uppercase tracking-[0.2em]">Загружаем заявки</span>
            </div>
          ) : leads.length === 0 ? (
            <p className="py-20 font-cormorant text-3xl font-light italic text-[#525252]" data-testid="admin-empty-state">
              Заявок пока нет — самое время поделиться ссылкой.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-leads-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-4 pr-6 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#525252]">№</th>
                    <th className="py-4 pr-6 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#525252]">Имя</th>
                    <th className="py-4 pr-6 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#525252]">Телефон</th>
                    <th className="py-4 pr-6 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#525252]">Примерки</th>
                    <th className="py-4 pr-6 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#525252]">Дата</th>
                    <th className="py-4" />
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => (
                    <tr
                      key={lead.id}
                      className="border-b border-white/5 transition-colors duration-300 hover:bg-white/[0.02]"
                      data-testid={`admin-lead-row-${i}`}
                    >
                      <td className="py-5 pr-6 font-jetbrains text-xs text-[#525252]">
                        {String(leads.length - i).padStart(2, "0")}
                      </td>
                      <td className="py-5 pr-6 text-base font-light text-[#F4F4F0]">
                        <span className="flex items-center gap-2">
                          {lead.name}
                          {lead.submissions > 1 && (
                            <span className="font-jetbrains text-[9px] text-[#525252]" data-testid={`admin-lead-dupes-${i}`}>
                              ×{lead.submissions}
                            </span>
                          )}
                          {lead.plan ? (
                            <span
                              className="border border-[#C2A878]/50 px-2 py-0.5 font-jetbrains text-[8px] uppercase tracking-[0.2em] text-[#C2A878]"
                              data-testid={`admin-master-badge-${i}`}
                            >
                              {lead.plan.title}{lead.plan.period ? ` · ${lead.plan.period}` : ""}{lead.plan.until ? ` · до ${formatDate(lead.plan.until)}` : ""}
                            </span>
                          ) : lead.is_master && (
                            <span
                              className="border border-[#C2A878]/50 px-2 py-0.5 font-jetbrains text-[8px] uppercase tracking-[0.2em] text-[#C2A878]"
                              data-testid={`admin-master-badge-${i}`}
                            >
                              Платная
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="py-5 pr-6 font-jetbrains text-sm text-[#C2A878]">{lead.phone}</td>
                      <td
                        className="py-5 pr-6 font-jetbrains text-xs text-[#A3A3A3]"
                        data-testid={`admin-tryon-remaining-${i}`}
                      >
                        осталось <span className="text-[#C2A878]">{lead.tryon_remaining ?? 5}</span>
                        {lead.tryon_used > 0 && (
                          <span className="text-[#525252]"> · использовано {lead.tryon_used}</span>
                        )}
                      </td>
                      <td className="py-5 pr-6 font-jetbrains text-xs text-[#A3A3A3]">{formatDate(lead.created_at)}</td>
                      <td className="py-5">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={amounts[lead.id] ?? 5}
                            onChange={(e) =>
                              setAmounts((prev) => ({ ...prev, [lead.id]: e.target.value }))
                            }
                            className="w-16 border border-white/15 bg-transparent px-2 py-1.5 text-center font-jetbrains text-xs text-[#F4F4F0] focus:border-[#C2A878] focus:outline-none"
                            data-testid={`admin-grant-amount-${i}`}
                          />
                          <button
                            onClick={() => grantTryon(lead, i)}
                            disabled={granting !== null}
                            className="flex items-center gap-1.5 border border-[#C2A878]/60 px-3 py-1.5 font-jetbrains text-[10px] uppercase tracking-[0.15em] text-[#C2A878] transition-colors duration-300 hover:bg-[#C2A878] hover:text-[#0A0A0A] disabled:opacity-50"
                            data-testid={`admin-grant-button-${i}`}
                          >
                            {granting === i ? <Loader2 size={11} className="animate-spin" /> : "Добавить"}
                          </button>
                          <button
                            onClick={() => toggleMaster(lead, i)}
                            disabled={masterToggling !== null}
                            className={`flex items-center gap-1.5 border px-3 py-1.5 font-jetbrains text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 disabled:opacity-50 ${
                              lead.is_master
                                ? "border-white/15 text-[#A3A3A3] hover:border-red-400/60 hover:text-red-400"
                                : "border-white/15 text-[#A3A3A3] hover:border-[#C2A878] hover:text-[#C2A878]"
                            }`}
                            data-testid={`admin-master-toggle-${i}`}
                          >
                            {masterToggling === i ? (
                              <Loader2 size={11} className="animate-spin" />
                            ) : lead.is_master ? (
                              "Убрать доступ"
                            ) : (
                              "Платный доступ"
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-16" data-testid="admin-usage-section">
          <p className="mb-6 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
            ( Пользователи примерок )
          </p>
          {!usageUsers || usageUsers.length === 0 ? (
            <p className="text-sm text-[#525252]">Пока никто не делал примерки.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-usage-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-3 pr-6 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#525252]">Пользователь</th>
                    <th className="py-3 pr-6 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#525252]">Примерки</th>
                    <th className="py-3 pr-6 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#525252]">Активность</th>
                    <th className="py-3" />
                  </tr>
                </thead>
                <tbody>
                  {usageUsers.map((u, i) => (
                    <tr key={u.key} className="border-b border-white/5" data-testid={`admin-usage-row-${i}`}>
                      <td className="py-4 pr-6 font-jetbrains text-xs text-[#F4F4F0]">{usageLabel(u)}</td>
                      <td className="py-4 pr-6 font-jetbrains text-xs text-[#A3A3A3]" data-testid={`admin-usage-remaining-${i}`}>
                        осталось <span className="text-[#C2A878]">{u.remaining}</span>
                        <span className="text-[#525252]"> · использовано {u.used}</span>
                      </td>
                      <td className="py-4 pr-6 font-jetbrains text-xs text-[#525252]">
                        {u.updated_at ? formatDate(u.updated_at) : "—"}
                      </td>
                      <td className="py-4">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={keyAmounts[u.key] ?? 5}
                            onChange={(e) =>
                              setKeyAmounts((prev) => ({ ...prev, [u.key]: e.target.value }))
                            }
                            className="w-16 border border-white/15 bg-transparent px-2 py-1.5 text-center font-jetbrains text-xs text-[#F4F4F0] focus:border-[#C2A878] focus:outline-none"
                            data-testid={`admin-usage-amount-${i}`}
                          />
                          <button
                            onClick={() => grantByKey(u, i)}
                            disabled={grantingKey !== null}
                            className="flex items-center gap-1.5 border border-[#C2A878]/60 px-3 py-1.5 font-jetbrains text-[10px] uppercase tracking-[0.15em] text-[#C2A878] transition-colors duration-300 hover:bg-[#C2A878] hover:text-[#0A0A0A] disabled:opacity-50"
                            data-testid={`admin-usage-grant-button-${i}`}
                          >
                            {grantingKey === i ? <Loader2 size={11} className="animate-spin" /> : "Добавить"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-16" data-testid="admin-plans-section">
          <p className="mb-2 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
            ( Тарифы )
          </p>
          <p className="mb-6 text-sm text-[#525252]">
            Цены и лимиты, которые видят пользователи в личном кабинете. «Про» открывает техкарты.
          </p>
          {!plans ? null : (
            <div className="grid gap-4 md:grid-cols-2">
              {plans.map((p, i) => (
                <div key={p.id} className="border border-white/10 p-5" data-testid={`admin-plan-card-${i}`}>
                  <div className="flex gap-2">
                    <input
                      value={p.title}
                      onChange={(e) => updatePlanField(i, "title", e.target.value)}
                      className="w-1/2 border border-white/15 bg-transparent px-2 py-1.5 font-jetbrains text-xs text-[#F4F4F0] focus:border-[#C2A878] focus:outline-none"
                      data-testid={`admin-plan-title-${i}`}
                    />
                    <input
                      value={p.period}
                      onChange={(e) => updatePlanField(i, "period", e.target.value)}
                      className="w-1/2 border border-white/15 bg-transparent px-2 py-1.5 font-jetbrains text-xs text-[#F4F4F0] focus:border-[#C2A878] focus:outline-none"
                      data-testid={`admin-plan-period-${i}`}
                    />
                  </div>
                  <div className="mt-2 flex gap-2">
                    <label className="flex-1">
                      <span className="font-jetbrains text-[9px] uppercase tracking-[0.2em] text-[#525252]">Цена ₽</span>
                      <input
                        type="number"
                        min="1"
                        value={p.price_rub}
                        onChange={(e) => updatePlanField(i, "price_rub", e.target.value)}
                        className="mt-1 w-full border border-white/15 bg-transparent px-2 py-1.5 font-jetbrains text-xs text-[#F4F4F0] focus:border-[#C2A878] focus:outline-none"
                        data-testid={`admin-plan-price-${i}`}
                      />
                    </label>
                    <label className="flex-1">
                      <span className="font-jetbrains text-[9px] uppercase tracking-[0.2em] text-[#525252]">Месяцев</span>
                      <input
                        type="number"
                        min="1"
                        value={p.months}
                        onChange={(e) => updatePlanField(i, "months", e.target.value)}
                        className="mt-1 w-full border border-white/15 bg-transparent px-2 py-1.5 font-jetbrains text-xs text-[#F4F4F0] focus:border-[#C2A878] focus:outline-none"
                        data-testid={`admin-plan-months-${i}`}
                      />
                    </label>
                    <label className="flex-1">
                      <span className="font-jetbrains text-[9px] uppercase tracking-[0.2em] text-[#525252]">Генераций</span>
                      <input
                        type="number"
                        min="0"
                        value={p.generations}
                        onChange={(e) => updatePlanField(i, "generations", e.target.value)}
                        className="mt-1 w-full border border-white/15 bg-transparent px-2 py-1.5 font-jetbrains text-xs text-[#F4F4F0] focus:border-[#C2A878] focus:outline-none"
                        data-testid={`admin-plan-gen-${i}`}
                      />
                    </label>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-1.5 font-jetbrains text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">
                        <input
                          type="checkbox"
                          checked={!!p.is_pro}
                          onChange={(e) => updatePlanField(i, "is_pro", e.target.checked)}
                          className="accent-[#C2A878]"
                          data-testid={`admin-plan-pro-${i}`}
                        />
                        Про
                      </label>
                      <label className="flex items-center gap-1.5 font-jetbrains text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">
                        <input
                          type="checkbox"
                          checked={!!p.active}
                          onChange={(e) => updatePlanField(i, "active", e.target.checked)}
                          className="accent-[#C2A878]"
                          data-testid={`admin-plan-active-${i}`}
                        />
                        Виден
                      </label>
                    </div>
                    <button
                      onClick={() => savePlan(p, i)}
                      disabled={planSaving !== null}
                      className="flex items-center gap-1.5 border border-[#C2A878]/60 px-3 py-1.5 font-jetbrains text-[10px] uppercase tracking-[0.15em] text-[#C2A878] transition-colors duration-300 hover:bg-[#C2A878] hover:text-[#0A0A0A] disabled:opacity-50"
                      data-testid={`admin-plan-save-${i}`}
                    >
                      {planSaving === i ? <Loader2 size={11} className="animate-spin" /> : "Сохранить"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-16" data-testid="admin-accounts-section">
          <p className="mb-2 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
            ( Пользователи · выдача тарифов )
          </p>
          <p className="mb-6 text-sm text-[#525252]">
            Найдите пользователя по Telegram, почте или телефону — и выдайте любой тариф бесплатно.
          </p>
          <div className="flex gap-2">
            <input
              value={accQuery}
              onChange={(e) => setAccQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchAccounts()}
              placeholder="@username, почта или телефон"
              className="flex-1 border border-white/15 bg-transparent px-4 py-3 font-jetbrains text-xs text-[#F4F4F0] placeholder:text-[#525252] focus:border-[#C2A878] focus:outline-none"
              data-testid="admin-account-search-input"
            />
            <button
              onClick={searchAccounts}
              disabled={accSearching || accQuery.trim().length < 2}
              className="flex items-center gap-2 border border-[#C2A878] px-6 py-3 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#C2A878] transition-colors duration-300 hover:bg-[#C2A878] hover:text-[#0A0A0A] disabled:opacity-50"
              data-testid="admin-account-search-button"
            >
              {accSearching ? <Loader2 size={12} className="animate-spin" /> : "Найти"}
            </button>
          </div>

          {accResults !== null && (
            <div className="mt-6">
              {accResults.length === 0 ? (
                <p className="text-sm text-[#525252]" data-testid="admin-account-empty">
                  Никого не нашли — проверьте запрос. Пользователь появляется после первого входа в кабинет.
                </p>
              ) : (
                <div className="space-y-3">
                  {accResults.map((a, i) => (
                    <div key={a.id} className="border border-white/10 p-5" data-testid={`admin-account-row-${i}`}>
                      <p className="font-jetbrains text-xs text-[#F4F4F0]">{accLabel(a)}</p>
                      <p className="mt-1.5 font-jetbrains text-[10px] uppercase tracking-[0.15em] text-[#525252]" data-testid={`admin-account-plan-${i}`}>
                        {a.plan
                          ? `${a.plan} · ${a.plan_period} · до ${formatDate(a.plan_until)}`
                          : "тариф не оформлен"}
                        {" · "}осталось {a.remaining}
                        {!a.plan && a.is_master ? " · платный доступ (вручную)" : ""}
                      </p>
                      <div className="mt-3 flex items-center gap-2">
                        <select
                          value={accPlanChoice[a.id] || (plans && plans[0]?.id) || ""}
                          onChange={(e) => setAccPlanChoice((prev) => ({ ...prev, [a.id]: e.target.value }))}
                          className="flex-1 border border-white/15 bg-[#0A0A0A] px-3 py-2.5 font-jetbrains text-xs text-[#F4F4F0] focus:border-[#C2A878] focus:outline-none"
                          data-testid={`admin-account-plan-select-${i}`}
                        >
                          {(plans || []).map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.title} · {p.period} · {p.generations} ген · {p.price_rub}₽
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => grantPlan(a, i)}
                          disabled={accGranting !== null}
                          className="flex items-center gap-1.5 border border-[#C2A878]/60 px-4 py-2.5 font-jetbrains text-[10px] uppercase tracking-[0.15em] text-[#C2A878] transition-colors duration-300 hover:bg-[#C2A878] hover:text-[#0A0A0A] disabled:opacity-50"
                          data-testid={`admin-account-grant-${i}`}
                        >
                          {accGranting === i ? <Loader2 size={11} className="animate-spin" /> : "Выдать"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <Link
          to="/"
          className="mt-16 inline-flex items-center gap-2 font-jetbrains text-xs uppercase tracking-[0.2em] text-[#525252] transition-colors duration-500 hover:text-[#C2A878]"
          data-testid="admin-back-to-site"
        >
          <ArrowLeft size={12} strokeWidth={1.5} />
          На сайт
        </Link>
      </div>
    </div>
  );
}
